/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { offEvent, onEvent } from "@adapptio/lib-shared";
import { IEditorApiService } from "../api/IEditorApiService";
import { BrowserLogger } from "../common/BrowserLogger";
import { IEditor } from "../IEditor";
import {
	IProductSupportOpts,
	IProductSupport_GettingStarted,
	IProductSupport_MenuItem,
	IProductSupport_ReferenceGuide,
	IProductSupport_SocialLink
} from "./IProductSupportOpts";

/**
 * Service options
 */
export interface IProductSupportServiceOpts extends IProductSupportOpts {}

/**
 * Service that provides product support features:
 */
export class ProductSupportService {
	/** Logger facility */
	private logger: BrowserLogger;

	/** API service instance */
	private apiService: IEditorApiService;

	private handleAuth: () => void;
	private handleUnauth: () => void;

	private supportMenu: IProductSupport_MenuItem[];
	private inspirationMenu: IProductSupport_MenuItem[];
	private gettingStarted: IProductSupport_GettingStarted;
	private referenceGuide: IProductSupport_ReferenceGuide;
	private socialLinks: IProductSupport_SocialLink[];
	private bugReportLink: string | null;

	/**
	 * Product Support Service Constructor
	 *
	 * @param dc Dependency Container
	 * @param opts Configuration options
	 */
	public constructor(dc: IEditor, private opts: IProductSupportServiceOpts) {
		this.logger = dc.get("logger").facility("ProductSupportService");
		this.apiService = dc.get("apiService");

		this.logger.info("Configuration:", opts);

		this.supportMenu = opts.supportMenu ?? [];
		this.inspirationMenu = opts.inspirationMenu ?? [];
		this.gettingStarted = opts.gettingStarted ?? {
			videos: [],
			links: []
		};
		this.referenceGuide = opts.referenceGuide ?? {};
		this.socialLinks = opts.socialLinks ?? [];
		this.bugReportLink = opts.bugReportLink ?? null;
	}

	/**
	 * Initializes service
	 */
	public async init(): Promise<void> {
		this.handleAuth = () => {
			// Dummy for now
		};

		this.handleUnauth = () => {
			// Dummy for now
		};

		onEvent(this.apiService.onAuthorize, this.handleAuth);
		onEvent(this.apiService.onUnauthorized, this.handleUnauth);

		if (this.apiService.getEditorIdentity()) {
			this.handleAuth();
		}
	}

	/**
	 * Disposes service
	 */
	public async dispose(): Promise<void> {
		offEvent(this.apiService.onAuthorize, this.handleAuth);
		offEvent(this.apiService.onUnauthorized, this.handleUnauth);
	}

	/**
	 * Returns item for a support menu
	 */
	public getSupportMenuItems(): IProductSupport_MenuItem[] {
		return this.supportMenu;
	}

	/**
	 * Returns item for an inspiration menu
	 */
	public getInspirationMenuItems(): IProductSupport_MenuItem[] {
		return this.inspirationMenu;
	}

	/**
	 * Returns options for a getting started screen
	 */
	public getGettingStartedOptions(): IProductSupport_GettingStarted {
		return this.gettingStarted;
	}

	/**
	 * Returns options for a reference guide screen
	 */
	public getReferenceGuideOptions(): IProductSupport_ReferenceGuide {
		return this.referenceGuide;
	}

	/**
	 * Returns a list of social links
	 */
	public getSocialLinks(): IProductSupport_SocialLink[] {
		return this.socialLinks;
	}

	/**
	 * Returns whenever a bug report link is configured
	 */
	public hasBugReportLink(): boolean {
		return this.bugReportLink !== null;
	}

	/**
	 * Returns a bug report link
	 */
	public getBugReportLink(): string | null {
		return this.bugReportLink;
	}
}
